import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaUtensils,
  FaBed,
  FaCamera
} from "react-icons/fa";

import { IoMdArrowDropright } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/banner1.jpg";
import imageDay1 from "../../assets/zanzibar1.jpg";
import imageDay2 from "../../assets/zanzibar2.jpg";
import imageDay3 from "../../assets/znz_bch.jpg";

const hero = {
  kicker: "8-Days Zanzibar Adventure",
  title: "Spice Island Escape",
  subtitle: "Stone Town culture meets idyllic coastal relaxation.",
  backgroundImage: imageHero,
  meta: ["8 Days", "Stone Town", "Beachfront", "All Year"],
};

const overview = `Imagine escaping to the tropical paradise of the Zanzibar archipelago where spice islands fill the air with enchanting aromas. Walk along white sand beaches, swim in turquoise Indian Ocean waters, and explore the cultural treasure of Stone Town. This UNESCO World Heritage site reveals centuries of African, Persian, Arabic, Indian, and European influence through its architecture, markets, and historic streets.`;

const highlights = [
  {
    icon: FaMapMarkerAlt,
    title: "Stone Town Discovery",
    description:
      "Explore UNESCO streets, carved doors, slave market, and Old Fort.",
    color: "#10b981",
  },
  {
    icon: FaStar,
    title: "Beach Paradise",
    description:
      "Five nights on white sand beaches with spectacular ocean views.",
    color: "#f59e0b",
  },
  {
    icon: FaPlane,
    title: "Swahili Cuisine",
    description:
      "Enjoy seafood curry, fish biryani, octopus salad, and spice-rich dishes.",
    color: "#3b82f6",
  },
  {
    icon: FaRegClock,
    title: "All-Year Adventure",
    description:
      "Perfect tropical escape available throughout the year.",
    color: "#8b5cf6",
  },
];

const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Locations",
    description: "Stone Town and Zanzibar Coast.",
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "8 days | Start/End: Zanzibar International Airport.",
  },
  {
    icon: FaUtensils,
    title: "Meals",
    description: "Breakfast daily with selected lunches and dinners.",
  },
  {
    icon: FaBed,
    title: "Accommodation",
    description: "Stone Town boutique hotels and Zanzibar beach resorts.",
  },
];

const itinerary = [
  {
    dayLabel: "Day 1-2",
    dayNum: "01",
    title: "Discover Ancient History and Exotic Flavours",
    image: imageDay1,
    body: "Upon arrival at Zanzibar International Airport you are transferred to your Stone Town hotel. Spend the afternoon exploring the aromatic streets and enjoying Swahili cuisine. The following day wander through Stone Town’s historic maze, admire carved wooden doors, visit the former slave market and Old Fort, and stroll along the seafront.",
    activities: "Meals Plan: Bed and Breakfast",
    tags: ["Arrival", "Stone Town", "Culture"],
  },
  {
    dayLabel: "Day 3-7",
    dayNum: "02",
    title: "Zanzibar Coast – Beach Escape",
    image: imageDay2,
    body: "Travel to Zanzibar’s world-famous coastline where five days of relaxation await. Enjoy sunrise dhow views, swim in the warm Indian Ocean, unwind by the pool, or indulge in a rejuvenating spa treatment. Optional excursions include spice tours, Prison Island visits, and exploring Jozani Forest.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Beach", "Relaxation", "Excursions"],
  },
  {
    dayLabel: "Day 8",
    dayNum: "03",
    title: "Departure Day",
    image: imageDay3,
    body: "Enjoy your final breakfast overlooking the ocean before transferring back to Zanzibar International Airport. Depart feeling refreshed after a week of culture, cuisine, and tropical relaxation.",
    activities: "Meals Plan: Breakfast",
    tags: ["Departure", "Airport Transfer"],
  },
];

export default function ZanzibarAdventure() {

  const appContext = useContext(AppContext);

  const theme = {
    primary: "#059669",
    primaryLight: "#10b981",
    accent: "#0d9488",
    bg: "#f8fafc",
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        color: theme.text,
        background: theme.bg
      }}
    >

      {/* HERO */}
      <section
        style={{
          height: "75vh",
          minHeight: "500px",
          backgroundImage: `linear-gradient(135deg, rgba(3,8,16,0.85), rgba(5,95,105,0.75)), url(${imageHero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          padding: "0 1.5rem"
        }}
      >
        <div style={{ maxWidth: "800px" }}>
          <span
            className="badge rounded-pill px-4 py-2 mb-4"
            style={{
              background: "rgba(16,185,129,0.2)",
              border: "1px solid rgba(16,185,129,0.4)",
              color: "#6ee7b7",
              fontWeight: "600"
            }}
          >
            {hero.kicker}
          </span>

          <h1 style={{ fontSize: "clamp(2.5rem,6vw,4rem)", fontWeight: "700", marginBottom: "1rem" }}>
            {hero.title}
          </h1>

          <p style={{ fontSize: "1.3rem", marginBottom: "2rem" }}>
            {hero.subtitle}
          </p>

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span
                key={i}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  padding: "0.5rem 1.25rem",
                  borderRadius: "50px"
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 1.5rem" }}>

        {/* OVERVIEW */}
        <section style={{ marginBottom: "5rem", textAlign: "center", maxWidth: "800px", margin: "0 auto 5rem" }}>
          <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.25rem)", fontWeight: "700", marginBottom: "1.5rem" }}>
            Experience Zanzibar
          </h2>

          <p style={{ fontSize: "1.1rem", lineHeight: 1.8, color: theme.textMuted }}>
            {overview}
          </p>

          {/* QUICK STATS */}
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center", marginTop: "2rem" }}>
            {details.map((d, i) => {
              const Icon = d.icon;
              return (
                <div
                  key={i}
                  style={{
                    background: theme.cardBg,
                    padding: "1rem 1.5rem",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: "200px"
                  }}
                >
                  <Icon size={22} style={{ color: theme.primary }} />
                  <div>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>{d.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section style={{ marginBottom: "5rem" }}>
          <div className="text-center mb-4">
            <h2 style={{ fontWeight: "700", fontSize: "clamp(1.75rem,4vw,2.25rem)" }}>
              Trip Highlights
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
              gap: "1.5rem"
            }}
          >
            {highlights.map((h, i) => {
              const Icon = h.icon;
              return (
                <article
                  key={i}
                  style={{
                    background: theme.cardBg,
                    padding: "1.75rem",
                    borderRadius: "16px",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)"
                  }}
                >
                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "12px",
                      background: `${h.color}15`,
                      color: h.color,
                      marginBottom: "1rem"
                    }}
                  >
                    <Icon />
                  </div>

                  <h3 style={{ fontWeight: "600", marginBottom: "0.5rem" }}>
                    {h.title}
                  </h3>

                  <p style={{ color: theme.textMuted }}>{h.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ITINERARY */}
        <section>
          <div className="text-center mb-5">
            <h2 style={{ fontWeight: "700", fontSize: "clamp(1.75rem,4vw,2.25rem)" }}>
              Your 8-Day Journey
            </h2>
          </div>

          {itinerary.map((day, i) => (
            <article
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1.3fr",
                gap: "2rem",
                marginBottom: "2rem",
                background: "white",
                borderRadius: "18px",
                overflow: "hidden",
                boxShadow: "0 8px 22px rgba(0,0,0,0.08)"
              }}
            >
              <div
                style={{
                  backgroundImage: `url(${day.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  minHeight: "260px"
                }}
              />

              <div style={{ padding: "2rem" }}>
                <h3 style={{ fontWeight: "700", marginBottom: "1rem" }}>
                  {day.title}
                </h3>

                <p style={{ color: theme.textMuted, marginBottom: "1rem", lineHeight: 1.7 }}>
                  {day.body}
                </p>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                  {day.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: `${theme.primary}10`,
                        color: theme.primary,
                        padding: "0.35rem 0.8rem",
                        borderRadius: "40px",
                        fontSize: "0.8rem"
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    background: `${theme.accent}10`,
                    padding: "0.5rem 0.9rem",
                    borderRadius: "8px",
                    fontSize: "0.9rem"
                  }}
                >
                  <FaUtensils size={14} />
                  {day.activities}
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* CTA */}
        <section
          style={{
            marginTop: "4rem",
            textAlign: "center",
            padding: "3rem",
            borderRadius: "20px",
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`
          }}
        >
          <FaCamera size={40} style={{ color: theme.primary, marginBottom: "1rem" }} />

          <h3 style={{ fontWeight: "700", fontSize: "1.8rem", marginBottom: "1rem" }}>
            Ready for Your Spice Island Escape?
          </h3>

          <p style={{ maxWidth: "600px", margin: "0 auto 2rem", color: theme.textMuted }}>
            Discover Zanzibar’s culture, cuisine, and beaches in one unforgettable journey.
          </p>

          <button
            className="btn btn-lg px-5 py-3 rounded-pill"
            style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
              border: "none",
              color: "white",
              fontWeight: "600",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
            onClick={()=>appContext.navTo({item:'plan-trip'})}
          >
            Book This Trip <IoMdArrowDropright size={20} />
          </button>
        </section>

      </div>
    </div>
  );
}